
import { computed, defineComponent, onMounted, ref } from 'vue'
import { ElMessage } from 'element-plus'
import PaymentAdminApi from '@/core/services/payment/PaymentAdminApi'
import { usePaginatedList } from '@/core/composables/usePaginatedList'
import { useDocumentActions } from '@/core/composables/useDocumentActions'
import { formatDate, formatMoney } from '@/core/helpers/paymentFormat'
import { documentStatusLabels, toOptions } from '@/core/services/payment/labels'
import { PaymentDocument } from '@/core/services/payment/types'
import PaymentListLayout from '@/core/components/payment/PaymentListLayout.vue'
import NewTabLink from '@/core/components/payment/NewTabLink.vue'
import PaymentStatusTag from '@/core/components/payment/PaymentStatusTag.vue'
import PaymentFormDialog from '@/core/components/payment/PaymentFormDialog.vue'
import { FormField } from '@/core/components/payment/formField'

/**
 * Счета и акты. Быстрый фильтр «Ждут оплаты» (счета в статусе issued) заменяет отдельную страницу:
 * это основной сценарий «клиент оплатил счёт»: сверить с банковской выпиской и подтвердить оплату.
 * Система сама переведёт заказ в paid, выдаст услуги, завершит заказ и выпустит акт.
 */
export default defineComponent({
  name: 'DocumentsList',
  components: {
    PaymentListLayout,
    PaymentStatusTag,
    NewTabLink,
    PaymentFormDialog,
  },
  setup() {
    const list = usePaginatedList<PaymentDocument>(
      PaymentAdminApi.documents.list,
      {
        defaults: {
          type: '',
          status: [],
          number: '',
          inn: '',
          user_id: '',
          order_id: '',
          issued_from: '',
          issued_to: '',
        },
      }
    )
    const actions = useDocumentActions(list.load)

    const isUnpaidPreset = computed(
      () =>
        list.filters.type === 'invoice' &&
        Array.isArray(list.filters.status) &&
        list.filters.status.length === 1 &&
        list.filters.status[0] === 'issued'
    )

    const showUnpaid = () => {
      list.filters.type = 'invoice'
      list.filters.status = ['issued']
      list.apply()
    }

    const confirmOpen = ref(false)
    const confirmInitial = ref<Record<string, string>>({})

    const confirmFields: FormField[] = [
      { key: 'number', label: 'Номер счёта', type: 'text', required: true },
      {
        key: 'paid_at',
        label: 'Дата оплаты по выписке',
        type: 'datetime',
        hint: 'Если не указать, будет текущее время.',
      },
      {
        key: 'reference',
        label: 'Номер платёжного поручения',
        type: 'text',
        hint: 'Повторное подтверждение с тем же номером безопасно.',
      },
    ]

    const openConfirm = (row: PaymentDocument) => {
      confirmInitial.value = { number: row.number }
      confirmOpen.value = true
    }

    const confirmAction = (payload: Record<string, any>) =>
      PaymentAdminApi.payments.confirmInvoice(payload as any)

    const onConfirmed = (result: {
      order: { number: number; status: string }
    }) => {
      const finished = result.order.status === 'completed'
      ElMessage({
        type: finished ? 'success' : 'info',
        message: finished
          ? `Оплата подтверждена, заказ № ${result.order.number} выполнен`
          : `Оплата подтверждена, заказ № ${result.order.number} в статусе «${result.order.status}». Проверьте заказ: услуги могли ещё не выдаться.`,
      })
      list.load()
    }

    const isOverdue = (row: PaymentDocument) =>
      row.status === 'issued' &&
      Boolean(row.due_at) &&
      new Date(row.due_at as string).getTime() < Date.now()

    onMounted(list.init)

    return {
      ...list,
      ...actions,
      isUnpaidPreset,
      showUnpaid,
      confirmOpen,
      confirmInitial,
      confirmFields,
      openConfirm,
      confirmAction,
      onConfirmed,
      isOverdue,
      statusOptions: toOptions(documentStatusLabels),
      money: formatMoney,
      date: formatDate,
    }
  },
})
